import React, { useState } from 'react';
import {
  BarList, Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, SubTabs, useAdminGet,
} from './kit';
import HomeSearches from './search/HomeSearches';
import Members from './search/Members';
import QueryExplorer from './search/QueryExplorer';
import SearchFlow from './search/SearchFlow';
import StepTimings from './search/StepTimings';

/**
 * Search Intel group — admin_v3 Searches page (overview, industry breakdown,
 * search log) + admin_panel_v2 Tab 21 "Saved & Search Activity" (top queries,
 * bookmarks, per-user rollup). One place for "what do users want".
 *
 * Query explorer and Step timings (components/admin/search/) read the
 * search_query_logs table the v2 engine writes — every call with its
 * per-step timings and the results shown — so "what took what" and "what
 * did this user actually see" have an answer here rather than in a psql
 * session. Search flow (search/SearchFlow.tsx) draws the live pipeline itself —
 * every step with its params, the paths the named scenarios take, and a
 * real query replayed step by step. The older Search log tab stays: it reads
 * user_direct_search_history, which also holds pre-v2 and pre-login searches.
 * Homepage searches (search/HomeSearches.tsx) is the public homepage's own
 * log: every anonymous search there and what the visitor clicked next.
 * Members (search/Members.tsx) ranks members by their searches: lifetime,
 * last X days, a picked period, with lifecycle and profile %.
 */

const TABS = ['Overview', 'Members', 'Query explorer', 'Homepage searches', 'Step timings', 'Search flow', 'Search log', 'Top queries',
  'Bookmarks', 'Per-user rollup'];

const SearchIntelPanel: React.FC = () => {
  const [tab, setTab] = useState(TABS[0]);
  return (
    <div className="flex flex-col gap-4">
      <SubTabs tabs={TABS} active={tab} onChange={setTab} />
      {tab === 'Overview' && <Overview />}
      {tab === 'Members' && <Members />}
      {tab === 'Query explorer' && <QueryExplorer />}
      {tab === 'Homepage searches' && <HomeSearches />}
      {tab === 'Step timings' && <StepTimings />}
      {tab === 'Search flow' && <SearchFlow />}
      {tab === 'Search log' && <SearchLog />}
      {tab === 'Top queries' && <TopQueries />}
      {tab === 'Bookmarks' && <Bookmarks />}
      {tab === 'Per-user rollup' && <Rollup />}
    </div>
  );
};

const Overview: React.FC = () => {
  const overview = useAdminGet('search', 'overview');
  const industries = useAdminGet('search', 'industry_breakdown');
  return (
    <div className="flex flex-col gap-4">
      {overview.loading ? <Loading /> : overview.data && (
        <KPIRow>
          <KPI label="Total searches" value={overview.data.total?.toLocaleString?.()} />
          <KPI label="Logged-in" value={overview.data.logged_in?.toLocaleString?.()} />
          <KPI label="With results" value={`${overview.data.with_results_pct}%`}
            hint={`${overview.data.with_results?.toLocaleString?.()} searches`} />
          <KPI label="Pre-login sessions" value={overview.data.prelogin_sessions?.toLocaleString?.()} />
          <KPI label="Blank sessions" value={overview.data.blank_sessions?.toLocaleString?.()}
            hint="opened, never typed" />
        </KPIRow>
      )}
      <Section title="What industries people search (keyword heuristic)">
        {industries.loading ? <Loading /> : (
          <BarList items={(industries.data || []).map((r: any) => ({ label: r.industry, value: r.count }))} />
        )}
      </Section>
    </div>
  );
};

const SearchLog: React.FC = () => {
  const [state, setState] = useState('All');
  // "All" uses search/recent, which carries the filter summary per row. Every
  // other state goes to search/log, which filters in SQL — filtering the
  // 500-row recent window in the browser could only ever find zero-result
  // searches that happened to be recent, which is not what the filter claims.
  const useServer = state !== 'All';
  const recent = useAdminGet('search', 'recent', {}, []);
  const server = useAdminGet('search', 'log', { state, limit: 500 }, [state]);
  const active = useServer ? server : recent;
  const rows = useServer
    ? (server.data || []).map((r: any) => ({
        when: r.Date, user: r.User, query: r.Query, state: r.State,
        backend: r.Backend, hits: r.Results,
      }))
    : (recent.data || []);

  return (
    <Section title={useServer ? `Searches — ${state.toLowerCase()}` : 'Recent searches (latest 500)'}
      right={<div className="flex gap-2">
        <Select value={state} onChange={setState}
          options={['All', 'Logged-in', 'Pre-login', 'With results', 'No results']} />
        <Btn small onClick={active.reload}>Refresh</Btn>
      </div>}>
      {active.error && <Msg kind="error">{active.error}</Msg>}
      {active.loading ? <Loading /> : (
        <DataTable rows={rows} searchable csvName="search_log"
          columns={useServer
            ? ['when', 'user', 'query', 'state', 'backend', 'hits']
            : ['when', 'user', 'query', 'filters', 'backend', 'hits']}
          empty={useServer ? `No ${state.toLowerCase()} searches recorded.` : 'No searches yet.'} />
      )}
    </Section>
  );
};

const TopQueries: React.FC = () => {
  const top = useAdminGet('search', 'top_queries');
  return (
    <Section title="Top queries (by times run)" right={<Btn small onClick={top.reload}>Refresh</Btn>}>
      {top.loading ? <Loading /> : (
        <DataTable rows={top.data || []} searchable csvName="top_queries" />
      )}
    </Section>
  );
};

const Bookmarks: React.FC = () => {
  const saved = useAdminGet('search', 'saved');
  const m = saved.data?.metrics;
  return (
    <div className="flex flex-col gap-4">
      {m && (
        <KPIRow>
          <KPI label="Total searches" value={m.searches?.toLocaleString?.()} />
          <KPI label="Distinct searchers" value={m.searchers} />
          <KPI label="Bookmarked techs" value={m.bookmarks} />
          <KPI label="Distinct bookmarkers" value={m.bookmarkers} />
          <KPI label="Liked" value={m.liked} />
        </KPIRow>
      )}
      <Section title="Most-bookmarked technologies">
        {saved.loading ? <Loading /> : (
          <DataTable rows={saved.data?.most_bookmarked || []} csvName="most_bookmarked" maxHeight="18rem" />
        )}
      </Section>
      <Section title="All bookmarks (latest 500)">
        {saved.loading ? <Loading /> : (
          <DataTable rows={saved.data?.bookmarks || []} searchable csvName="bookmarks" />
        )}
      </Section>
    </div>
  );
};

const Rollup: React.FC = () => {
  const rollup = useAdminGet('search', 'rollup');
  return (
    <Section title="Activity per user (searches · bookmarks · likes · contacts)"
      right={<Btn small onClick={rollup.reload}>Refresh</Btn>}>
      {rollup.loading ? <Loading /> : (
        <DataTable rows={rollup.data || []} searchable csvName="user_activity_rollup" />
      )}
    </Section>
  );
};

export default SearchIntelPanel;
